import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, User, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import SiteLayout from "@/components/site/SiteLayout";
import Seo, { articleJsonLd } from "@/components/site/Seo";
import { fetchBlogPost, toAbsolute } from "@/lib/api";
import ShareButtons from "@/components/site/ShareButtons";

function Paragraphs({ text }) {
  return text.split("\n").filter(Boolean).map((line, i) => {
    if (line.startsWith("## ")) return <h2 key={i} className="text-2xl font-extrabold mt-8 mb-3">{line.replace("## ", "")}</h2>;
    if (line.startsWith("- ")) return <li key={i} className="text-slate-300 leading-relaxed ms-5 list-disc">{line.replace("- ", "")}</li>;
    return <p key={i} className="text-slate-300 leading-relaxed mb-4 text-lg">{line}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const { t, dir } = useLang();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const Back = dir === "rtl" ? ArrowRight : ArrowLeft;

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchBlogPost(slug).then((r) => setPost(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  return (
    <SiteLayout>
      {notFound ? (
        <div className="min-h-[50vh] grid place-items-center text-slate-400">404 — {t.blog.empty}</div>
      ) : !post ? (
        <div className="min-h-[50vh] grid place-items-center"><Loader2 className="animate-spin text-primary" /></div>
      ) : (
        <article className="pb-24" dir="auto" data-testid="blog-post">
          <Seo
            title={post.title}
            description={post.excerpt}
            jsonLd={articleJsonLd({
              title: post.title,
              description: post.excerpt,
              image: toAbsolute(post.cover_image),
              url: typeof window !== "undefined" ? window.location.href : "",
              author: post.author,
              datePublished: post.created_at,
            })}
            jsonLdId="seo-jsonld-article"
          />
          <div className="relative h-[45vh] min-h-[320px] overflow-hidden">
            <img src={toAbsolute(post.cover_image)} alt={post.title} className="h-full w-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(5,9,20,0.4), var(--bg))" }} />
            <div className="absolute bottom-0 inset-x-0">
              <div className="mx-auto max-w-3xl px-5 sm:px-8 pb-8">
                {post.category && <span className="rounded-full px-3 py-1 text-xs font-bold text-[#04120a]" style={{ background: "var(--primary)" }}>{post.category}</span>}
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-4 leading-tight">{post.title}</h1>
                <div className="flex items-center gap-5 text-sm text-slate-400 mt-4">
                  <span className="flex items-center gap-1.5"><User size={14} /> {t.blog.by} {post.author}</span>
                  <span className="flex items-center gap-1.5"><Clock size={14} /> {post.read_time}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mx-auto max-w-3xl px-5 sm:px-8 mt-10">
            <p className="text-xl text-slate-300 leading-relaxed mb-8 font-medium border-s-2 ps-5" style={{ borderColor: "var(--primary)" }}>{post.excerpt}</p>
            <Paragraphs text={post.content} />
            <div className="mt-10 pt-6 border-t border-white/10">
              <ShareButtons title={post.title} testid="blog-share" shareType="blog" shareId={post.slug} />
            </div>
            <Link to="/blog" className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-bold hover:bg-white/5 transition-colors" data-testid="blog-back">
              <Back size={16} /> {t.blog.back}
            </Link>
          </div>
        </article>
      )}
    </SiteLayout>
  );
}
